<template>
    <v-expansion-panels
        v-if="selectedGroupBy !== 'none'"
        v-model="panels"
        class="hotel-grouped-room-offers"
        multiple
        variant="accordion"
    >
        <v-expansion-panel
            v-for="(rooms, index) in groupedRooms"
            :key="`group-${index}`"
            :class="isMultiRoom ? 'my-2' : ''"
        >
            <v-expansion-panel-title
                :class="$breakpoint.smAndDown ? 'text-body-2' : 'text-subtitle-1'"
                color="blue-grey-lighten-5"
                class="hotel-grouped-room-offers__title d-flex align-baseline text-nobreak"
            >
                <hotel-room-name-label
                    v-if="selectedGroupBy === 'name'"
                    :hotel="offer"
                    :room="rooms[0]"
                    :compare-mode="compareMode"
                    class="flex-shrink-1"
                >
                    <v-icon start size="small" class="text-info" @click.native.stop="roomInfo(offer, rooms[0])">
                        mdi-information-outline
                    </v-icon>
                    {{ rooms[0].name }}
                </hotel-room-name-label>
                <div v-else class="flex-shrink-1">
                    {{ $t(`filters_mealTypes.${rooms[0].mealTypeStandardCode}`) }}
                </div>
                <strong class="pl-2 pr-4 flex-shrink-0 text-right">
                    <v-chip
                        v-if="selectedGroupBy !== 'mealTypeStandardCode'"
                        label
                        size="x-small"
                        :color="minPriceRoomOffer(rooms).available ? 'green-darken-1' : '#d79600'"
                        class="mr-4"
                        :class="{
                            available: minPriceRoomOffer(rooms).available,
                            request: !minPriceRoomOffer(rooms).available,
                        }"
                    >
                        {{ minPriceRoomOffer(rooms).available ? $t('available') : $t('on_request') }}
                    </v-chip>
                    <template v-if="minPriceRoomOffer(rooms).price && showPrice">
                        {{ $t('from') }} {{ $filters.price(minPriceRoomOffer(rooms).price) }}
                    </template>
                </strong>
            </v-expansion-panel-title>
            <v-expansion-panel-text :class="isMultiRoom ? 'border' : ''">
                <div class="d-flex flex-column flex-md-row align-center">
                    <v-img
                        v-if="getImgSrc(rooms[0])"
                        tile
                        class="mx-md-4 cursor-pointer"
                        :src="$filters.imageUrl(getImgSrc(rooms[0]), 300)"
                        :alt="rooms[0].name"
                        eager
                        :width="$breakpoint.smAndDown ? '100%' : 150"
                        :max-width="$breakpoint.smAndDown ? '100%' : 150"
                        height="100"
                        :lazy-src="$filters.srcHost('/placeholder.png')"
                        @click="roomInfo(offer, rooms[0])"
                    />
                    <div
                        class="hotel-room-offers__group-items full-width"
                        :class="getImgSrc(rooms[0]) ? 'side-border' : ''"
                    >
                        <div v-for="(room, roomIndex) in rooms.slice(0, expanded)" :key="`room-${roomIndex}-${index}`">
                            <div class="d-flex">
                                <slot name="radio" :room="room" />
                                <hotel-room
                                    :group-by="selectedGroupBy"
                                    :search-request="searchRequest"
                                    :room="room"
                                    :hotel="offer"
                                    :rph="1"
                                    :class="isMultiRoom ? 'py-2' : 'pa-4'"
                                    :compare-mode="compareMode"
                                    :data-cy="`room-offer-card-${roomIndex}`"
                                >
                                    <template v-slot:price>
                                        <slot name="price" :room="room" />
                                    </template>
                                </hotel-room>
                            </div>
                            <v-divider v-if="roomIndex !== rooms.length - 1" />
                        </div>
                    </div>
                </div>
                <div
                    v-if="rooms.length > expanded"
                    class="hotel-grouped-room-offers__show-more d-flex my-4 justify-center"
                >
                    <v-btn variant="flat" size="small" color="primary" @click.stop="expanded = expanded + 5">
                        {{ $t('show_more_rooms') }}
                    </v-btn>
                </div>
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script lang="ts">
    import {Component, Prop, VModel, Vue, Watch} from 'vue-facing-decorator'
    import {EventBus, SEARCH_EVENT, SHOW_ROOM_INFO_MODAL_EVENT} from '@/utils/event-bus'
    import DiscountLabel from '~src/components/snippets/discountLabel.src'
    import ConditionsLabel from '~src/components/snippets/conditionsLabel.src'
    import CommissionAmount from '@/components/snippets/CommissionAmount.vue'
    import CorporatePolicyViolation from '@/components/snippets/CorporatePolicyViolation.vue'
    import HotelRoomOfferPriceBlock from '~src/components/search/offers/hotelRoomOfferPriceBlock.src'
    import SupplierPrice from '~src/components/snippets/supplierPrice.src'
    import HotelPriceNote from '@/components/hotels/snippets/HotelPriceNote.vue'
    import HotelMealTypeLabel from '~src/components/hotels/snippets/hotelMealTypeLabel.src'
    import TariffLabel from '~src/components/hotels/snippets/tariffLabel.src'
    import HotelRoom from '~src/components/hotels/search/offers/hotelRoom.src'
    import HotelRoomNameLabel from '@/components/hotels/snippets/HotelRoomNameLabel.vue'

    @Component({
        components: {
            HotelRoom,
            HotelMealTypeLabel,
            HotelPriceNote,
            DiscountLabel,
            ConditionsLabel,
            CommissionAmount,
            CorporatePolicyViolation,
            HotelRoomOfferPriceBlock,
            SupplierPrice,
            TariffLabel,
            HotelRoomNameLabel,
        },
    })
    export class HotelGroupedRoomOffers extends Vue {
        @VModel({default: () => []}) selectedOfferKeys
        @Prop({default: () => []}) rooms
        @Prop() offer
        @Prop({required: true}) searchRequest
        @Prop({default: false}) compareMode

        expanded = 5
        panels = [0]
        selectedOffers = []
        roomGroupIds = []
        firstChosenRoom = null

        @Watch('selectedGroupBy')
        onGroupByChanged() {
            this.panels = [0]
        }

        mounted() {
            EventBus.$on(SEARCH_EVENT, this.clearSelectedOfferKeys)
        }

        beforeUnmount() {
            EventBus.$off(SEARCH_EVENT, this.clearSelectedOfferKeys)
        }

        getImgSrc(room) {
            if (this.selectedGroupBy !== 'name') return null
            const hotelInfo = hotelsRuntimeStore.getHotelsInfoByRoomOffer(room, this.offer) || {}
            const roomType = hotelInfo?.hotelDescriptionInfo?.roomTypes?.roomType.find(
                roomType => roomType.code === room.code || roomType.code === room.roomCode
            )
            return roomType?.images.image[0]?.url
        }

        clearSelectedOfferKeys() {
            this.selectedOfferKeys = []
        }

        roomInfo(hotel, room) {
            EventBus.$emit(SHOW_ROOM_INFO_MODAL_EVENT, {hotel, room})
        }

        minPriceRoomOffer(rooms) {
            return hotelsRuntimeStore.minPriceRoomOffer({rooms})
        }

        get groupedRooms() {
            const mealTypesSortOrder = ['RO', 'BB', 'HB', 'FB', 'AI', 'BBT', 'HBT', 'FBT', 'other']
            const result = {}

            this.rooms.forEach(room => {
                if (!result[room[this.selectedGroupBy]]) {
                    result[room[this.selectedGroupBy]] = []
                }
                result[room[this.selectedGroupBy]].push(room)
            })

            const arr = Object.values(result)
            arr.sort((a, b) => {
                const aIndex = mealTypesSortOrder.indexOf(a[0].mealTypeStandardCode)
                const bIndex = mealTypesSortOrder.indexOf(b[0].mealTypeStandardCode)
                return aIndex - bIndex
            })

            return arr
        }

        get selectedGroupBy() {
            return hotelsStore.groupOffersBy
        }

        set selectedGroupBy(val) {
            hotelsStore.SET_GROUP_OFFERS_BY(val)
        }

        get isMultiRoom() {
            return this.searchRequest.rooms?.length > 1
        }

        get showPrice() {
            return true
        }
    }

    export default HotelGroupedRoomOffers
</script>

<style scoped lang="scss">
    @use 'vuetify/settings';
    @use 'sass:map';

    .title {
        text-transform: capitalize;
    }

    :deep(div) {
        .v-select__selections {
            font-size: 13px;
        }

        .v-list-item--link {
            font-size: 12px;
        }

        .v-list {
            padding: 0;
        }

        .v-expansion-panel-text__wrapper {
            padding-bottom: 0;
        }
    }

    .border {
        border-color: rgba(map.get(settings.$shades, 'black'), 0.12);
        border-style: solid;
        border-left-width: 1px;
        border-right-width: 1px;
        border-bottom-width: 1px;
        border-top-width: 0;
    }

    .side-border {
        border-left: 1px solid rgba(map.get(settings.$shades, 'black'), 0.12);
    }
</style>
